import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, IdentificationCard, Truck, CurrencyDollar, Clock, Gift } from "@phosphor-icons/react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import SmsConsentCheckbox from "@/components/shared/SmsConsentCheckbox";
import { submitDriverApply } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";

const DRIVER_IMG = "https://images.pexels.com/photos/7363190/pexels-photo-7363190.jpeg";

const REQS = [
  { icon: IdentificationCard, label: "Valid government ID" },
  { icon: IdentificationCard, label: "Valid driver's permit / license" },
  { icon: Truck, label: "Insured vehicle (motorcycle, car, van)" },
  { icon: CheckCircle, label: "Smartphone with data" },
];

const PERKS = [
  { icon: Clock, title: "Flexible hours", desc: "Work mornings, evenings or weekends — you're in control." },
  { icon: CurrencyDollar, title: "Weekly payouts", desc: "Reliable, transparent earnings paid directly into your account." },
  { icon: Gift, title: "Bonus incentives", desc: "Performance bonuses, referral rewards and surge earnings." },
];

const INIT = { full_name: "", email: "", phone: "", location: "", vehicle_type: "motorcycle", has_license: true, license_number: "", vehicle_plate: "", notes: "", sms_consent: false };

export default function Drivers() {
  const [form, setForm] = useState(INIT);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useSeo({
    title: "Become a Driver",
    description: "Drive with IslandHop in Trinidad & Tobago. Flexible hours, weekly payouts and the support of a real tech platform.",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitDriverApply(form);
      toast.success(res.message);
      setSubmitted(true);
      setForm(INIT);
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="drivers-page">
      <section className="relative pt-20 lg:pt-28 pb-16 border-b border-ih-line overflow-hidden">
        <div className="absolute inset-0 ih-grid-bg opacity-40 [mask-image:linear-gradient(180deg,#000,transparent)]" />
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] ih-radial-neon" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7 space-y-6">
            <SectionEyebrow label="Become a Driver" color="neon" />
            <h1 className="font-display font-black text-5xl lg:text-7xl text-white leading-[0.95] text-balance">
              Drive when you want.<br />
              <span className="text-ih-neon">Earn on your terms.</span>
            </h1>
            <p className="text-white/70 text-lg max-w-xl leading-relaxed">
              Join IslandHop&apos;s growing network of independent delivery contractors across Trinidad &amp; Tobago.
              Fast onboarding, weekly payouts, and the support of a real tech platform.
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a href="#apply" data-testid="drivers-hero-cta" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ih-neon text-black font-semibold hover:scale-[1.02] transition ih-shadow-neon">
                Apply now
              </a>
              <a href="#requirements" className="text-sm text-white/60 hover:text-white transition underline underline-offset-4 decoration-ih-line2">
                See requirements
              </a>
            </div>
            <a
              href="/pros"
              data-testid="drivers-pro-banner"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-ih-surface border border-ih-teal/40 text-sm text-white/80 hover:text-white hover:border-ih-teal transition"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-ih-teal" />
              Offering handyman, cleaning, tech support or events? Apply as a Service Pro →
            </a>
          </motion.div>

          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-ih-line2 ih-shadow-neon">
              <img src={DRIVER_IMG} alt="Driver" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ih-ink via-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid md:grid-cols-3 gap-4">
          {PERKS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="bg-ih-surface border border-ih-line rounded-2xl p-7 hover:border-ih-neon/40 transition">
                <Icon weight="duotone" size={28} className="text-ih-neon mb-4" />
                <p className="font-display font-bold text-xl text-white mb-2">{p.title}</p>
                <p className="text-white/60 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Requirements + form */}
      <section id="requirements" className="py-20 border-t border-ih-line">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24 self-start">
            <SectionEyebrow label="What you'll need" color="neon" />
            <h2 className="font-display font-extrabold text-4xl text-white leading-[1.05]">
              Simple requirements.<br />
              <span className="text-white/40">Fast onboarding.</span>
            </h2>
            <ul className="space-y-3 pt-2">
              {REQS.map((r) => {
                const Icon = r.icon;
                return (
                  <li key={r.label} className="flex items-center gap-3 text-white/80">
                    <Icon weight="duotone" size={20} className="text-ih-neon" />
                    {r.label}
                  </li>
                );
              })}
            </ul>
            <p className="text-sm text-white/40 pt-4">
              All drivers operate as independent contractors. Background and document verification required before activation.
            </p>
          </div>

          {/* FORM */}
          <div id="apply" className="lg:col-span-7">
            {submitted ? (
              <SuccessCard onReset={() => setSubmitted(false)} testId="drivers-success" />
            ) : (
              <form onSubmit={onSubmit} data-testid="driver-apply-form" className="bg-ih-surface border border-ih-line rounded-3xl p-8 lg:p-10 space-y-5">
                <h3 className="font-display font-extrabold text-2xl text-white">Driver application</h3>
                <p className="text-sm text-white/55 -mt-3">We&apos;ll reach out within 2 business days.</p>

                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Full name *">
                    <input data-testid="driver-name" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Email *">
                    <input type="email" data-testid="driver-email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Phone *">
                    <input data-testid="driver-phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="+1 868 ..." />
                  </Field>
                  <Field label="Location *">
                    <input data-testid="driver-location" required value={form.location} onChange={(e) => update("location", e.target.value)} className={inputCls} placeholder="Port of Spain" />
                  </Field>
                  <Field label="Vehicle type *">
                    <select data-testid="driver-vehicle" value={form.vehicle_type} onChange={(e) => update("vehicle_type", e.target.value)} className={inputCls}>
                      <option value="motorcycle">Motorcycle</option>
                      <option value="car">Car</option>
                      <option value="bicycle">Bicycle</option>
                      <option value="van">Van</option>
                      <option value="truck">Truck</option>
                    </select>
                  </Field>
                  <Field label="Do you have a valid license?">
                    <select data-testid="driver-license" value={form.has_license ? "yes" : "no"} onChange={(e) => update("has_license", e.target.value === "yes")} className={inputCls}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </Field>
                </div>

                <Field label="Anything else we should know?">
                  <textarea data-testid="driver-notes" rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} className={`${inputCls} resize-none`} />
                </Field>

                <SmsConsentCheckbox
                  checked={form.sms_consent}
                  onChange={(v) => update("sms_consent", v)}
                  accent="neon"
                  testId="driver-sms-consent"
                />

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="driver-submit"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-ih-neon text-black font-semibold hover:scale-[1.02] disabled:opacity-60 transition w-full sm:w-auto justify-center"
                >
                  {loading ? "Submitting..." : "Submit application"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-ih-line2 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-ih-neon transition text-sm";

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs uppercase tracking-[0.16em] text-white/50">{label}</span>
    {children}
  </label>
);

const SuccessCard = ({ onReset, testId }) => (
  <div data-testid={testId} className="bg-ih-surface border border-ih-neon/40 rounded-3xl p-10 lg:p-14 text-center ih-shadow-neon">
    <CheckCircle weight="duotone" size={56} className="mx-auto text-ih-neon mb-5" />
    <h3 className="font-display font-extrabold text-3xl text-white mb-3">Application received</h3>
    <p className="text-white/65 max-w-md mx-auto leading-relaxed">
      Welcome to IslandHop. Our onboarding team will reach out within 2 business days with next steps.
    </p>
    <button onClick={onReset} className="mt-6 text-sm text-ih-neon underline underline-offset-4">Submit another</button>
  </div>
);
